import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useGetMyBalanceHistoryQuery } from '../../api/balanceHistoryApi';
import BalanceHistoryTable from './components/BalanceHistoryTable';
import { AbsenceCategory } from '../../constants/absenceEnums';

const EmployeeBalanceHistoryPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [category, setCategory] = useState<AbsenceCategory | ''>('');

  const paginationParams = useMemo(() => ({
    pageNumber: page,
    pageSize: pageSize,
  }), [page, pageSize]);

  const filters = useMemo(() => ({
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    category: category || undefined,
  }), [fromDate, toDate, category]);

  const {
    data: balanceHistory,
    error,
    isLoading,
  } = useGetMyBalanceHistoryQuery({
    paginationParams,
    ...filters,
  });

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setCategory('');
    setPage(1);
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load balance history. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          My Balance History
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Track all changes to your absence balances over time.
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value as AbsenceCategory | '')}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value={AbsenceCategory.Vacation}>Vacation</MenuItem>
                  <MenuItem value={AbsenceCategory.SickDays}>Sick Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                fullWidth
                size="medium"
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Balance History Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <BalanceHistoryTable
            history={balanceHistory?.items || []}
            showEmployeeColumn={false}
            page={page}
            pageSize={pageSize}
            totalCount={balanceHistory?.totalCount || 0}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </Box>
  );
};

export default EmployeeBalanceHistoryPage;


